import Box from '@mui/material/Box'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import { useCreateApplication } from 'api/application/hooks'
import {
  getAddApplicationPayload,
  getEmptyAddApplication,
} from 'api/application/utils'
import Button from 'components/shared/Button'
import Modal from 'components/shared/Modal'
import { FormProvider, useForm } from 'react-hook-form'
import { usePublisherStore } from 'store/publisher'
import AppDetails from './AppDetails'
import { useStepper } from './hooks'
import Placement from './Placement'
import VirtualCurrency from './VirtualCurrency'

interface Props {
  open: boolean
  onClose: () => void
}

const AddModal = ({ open, onClose }: Props) => {
  const publisherId = usePublisherStore((state) => state.publisherId)

  const steps = [
    {
      label: 'App Details',
      component: <AppDetails />,
    },
    {
      label: 'Virtual Currency',
      component: <VirtualCurrency />,
    },
    {
      label: 'Placement',
      component: <Placement />,
    },
  ]

  const { activeStep, gotoStep, renderStep } = useStepper(steps)
  const firstStep = activeStep === 0
  const lastStep = activeStep === steps.length - 1

  const form = useForm({
    defaultValues: { ...getEmptyAddApplication() },
    mode: 'onBlur',
  })

  const { getValues } = form
  const { dirtyFields, errors, isValid: formIsValid } = form.formState

  const { mutate } = useCreateApplication()

  async function saveApp(variables) {
    if (formIsValid) {
      const payload = getAddApplicationPayload(variables, publisherId)

      mutate(payload)
      handleClose()
    }
  }

  function handleClose() {
    form.reset()
    gotoStep(0)
    onClose()
  }

  const isStepDisabled = (): boolean => {
    // check for mandatory and/or invalid fields on current tab

    switch (activeStep) {
      case 0:
        return (
          !dirtyFields.platform ||
          !dirtyFields.name ||
          !!errors.name ||
          (!dirtyFields.notInStore &&
            (!dirtyFields.storeUrl || !!errors.storeUrl))
        )
      case 1:
        return !dirtyFields.virtualCurrencyAttributes?.name
      case 2:
        return !(getValues('placementAttributes') as any)?.exchangeRate
      default:
        return !formIsValid
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Modal.Header>Add Application</Modal.Header>
      <Modal.Body>
        <Stepper alternativeLabel activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(saveApp)}>{renderStep()}</form>
        </FormProvider>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {!firstStep && (
            <Button
              variant="secondary"
              onClick={() => gotoStep(activeStep - 1)}
            >
              Previous
            </Button>
          )}
          {!lastStep ? (
            <Button
              disabled={isStepDisabled()}
              onClick={() => gotoStep(activeStep + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              disabled={isStepDisabled()}
              onClick={form.handleSubmit(saveApp)}
            >
              Save
            </Button>
          )}
        </Box>
      </Modal.Footer>
    </Modal>
  )
}

export default AddModal
